import Cookies from 'js-cookie';
import AppConfig from '@/AppConfig';
import { EAppFeature } from '@/constants';

export interface DeviceDetails {
  applicationID: string;
  os: string;
  flavour: string;
  versionName: string;
  versionCode: string;
  osVersion: string;
}

export enum EDeviceOS {
  ANDROID = 'ANDROID',
  IOS = 'IOS',
}

const DEVICE_OS_VALUES = new Set<string>(Object.values(EDeviceOS));

const parseDeviceDetails = (): DeviceDetails | null => {
  try {
    const cookieValue = Cookies.get('device-details');
    if (!cookieValue) return null;
    return JSON.parse(cookieValue) as DeviceDetails;
  } catch (error) {
    console.error('Failed to parse device-details cookie', error);
    return null;
  }
};

export const getDeviceVersionCode = (): number | null => {
  const parsed = parseDeviceDetails();
  if (!parsed?.versionCode) return null;
  const versionCode = parseInt(parsed.versionCode, 10);
  return isNaN(versionCode) ? null : versionCode;
};

export const getDeviceVersionName = (): string | null => {
  const parsed = parseDeviceDetails();
  const versionName = parsed?.versionName?.trim();
  return versionName || null;
};

export const getDeviceOS = (): EDeviceOS | null => {
  const parsed = parseDeviceDetails();
  if (!parsed?.os) return null;
  const normalized = parsed.os.toUpperCase();
  return DEVICE_OS_VALUES.has(normalized) ? (normalized as EDeviceOS) : null;
};

export type RHIPlatform = 'ANDROID' | 'IOS' | 'WEB';

export const getPlatformHeader = (): RHIPlatform => {
  return getDeviceOS() ?? 'WEB';
};

const MIN_SUPPORTED_VERSION_CONFIG: Record<EAppFeature, Record<EDeviceOS, number>> = {
  [EAppFeature.RHI_SELF_CHECKOUT]: {
    [EDeviceOS.IOS]: AppConfig.RHI_APP_MIN_SUPPORTED_VERSION_CODE_IOS,
    [EDeviceOS.ANDROID]: AppConfig.RHI_APP_MIN_SUPPORTED_VERSION_CODE_ANDROID,
  },
};

export const isBelowMinSupportedVersion = (feature: EAppFeature): boolean => {
  const deviceVersionCode = getDeviceVersionCode();
  if (deviceVersionCode === null) return false;

  const deviceOS = getDeviceOS();
  if (deviceOS === null) return false;

  const minVersion = MIN_SUPPORTED_VERSION_CONFIG[feature][deviceOS];
  return deviceVersionCode < minVersion;
};